import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from '@/lib/auth/use-auth'
import { useReference } from '@/lib/reference/use-reference'
import type { Airport, AllowRow } from '@/lib/reference'
import {
  exceptionIndex,
  fleetColumns,
  resolveAirport,
  verdictCounts,
} from '@/lib/reference/fleet'
import { airportGroupIndex } from '@/lib/reference/groups'
import { formatLocal, formatOffset, zoneStateAt } from '@/lib/time'
import { ReferenceTable, type Column } from '@/components/reference/ReferenceTable'
import { CapabilitySummary } from '@/components/reference/CapabilitySummary'
import { ExceptionDialog } from '@/components/reference/ExceptionDialog'

/** `g:<CODE>` for a group, or the issues list. Groups own their own names, so
    the prefix keeps a group called ISSUES from colliding with the view. */
const ISSUES = 'issues'

const TYPE_LABEL: Record<string, string> = {
  large_airport: 'Large',
  medium_airport: 'Medium',
  small_airport: 'Small',
}

/**
 * The station table, split domestic / international.
 *
 * DOM is Vietnam and ITL is everywhere else. The split is not cosmetic: it is
 * what decides which minimum ground time and which city pair limit apply to a
 * turnaround, so the two lists are separate views rather than a filter buried
 * in a column.
 *
 * Every flight leg names two of these rows, and each row names a timezone —
 * that chain is what puts a bar in the right place on the Gantt. The chain
 * has known breaks in the shipped drop, so the issues view exists to make
 * them findable rather than to hide them.
 *
 * The views are **groups** (Settings -> Groups), not a fixed set: DOM, ITL
 * and LIMITED are built in, and a group the operator adds gets a tab of its
 * own here the moment it exists.
 */
export function AirportsPage() {
  const { can } = useAuth()
  const {
    airports,
    timezones,
    countries,
    aircraft,
    airportCapability,
    capabilityException,
    airportGroups,
    zoneRules,
    create,
    update,
    remove,
  } = useReference()
  const [view, setView] = useState('g:DOM')
  const [exception, setException] = useState<Airport | null>(null)
  const [now, setNow] = useState(() => Date.now())

  const editable = can('settings.edit')

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(timer)
  }, [])

  const zoneNames = useMemo(() => timezones.map((row) => row.name).sort(), [timezones])
  const countryCodes = useMemo(() => countries.map((row) => row.code).sort(), [countries])

  const duplicateIata = useMemo(() => {
    const counts = new Map<string, number>()
    for (const airport of airports) {
      if (airport.iata) counts.set(airport.iata, (counts.get(airport.iata) ?? 0) + 1)
    }
    return new Set([...counts].filter(([, count]) => count > 1).map(([code]) => code))
  }, [airports])

  const knownCountries = useMemo(() => new Set(countries.map((row) => row.code)), [countries])

  /** Broken joins and ambiguous keys — everything that would make a leg
      resolve to the wrong station, or to no station at all. */
  const issueOf = useCallback(
    (airport: Airport): string | null => {
      if (!airport.timezone) return 'No timezone'
      if (!zoneRules[airport.timezone]) return 'Timezone not in table'
      if (!knownCountries.has(airport.countryCode)) return 'Country not in table'
      if (duplicateIata.has(airport.iata)) return 'IATA code used twice'
      return null
    },
    [zoneRules, knownCountries, duplicateIata],
  )

  const withIssues = useMemo(
    () => airports.filter((row) => issueOf(row) !== null),
    [airports, issueOf],
  )

  const fleet = useMemo(() => fleetColumns(aircraft), [aircraft])
  const exceptions = useMemo(
    () => exceptionIndex(capabilityException, 'airport'),
    [capabilityException],
  )
  const groups = useMemo(
    () => airportGroupIndex(airportGroups, airports, airportCapability),
    [airportGroups, airports, airportCapability],
  )

  const baselineFor = useCallback(
    (iata: string) => (column: string) =>
      resolveAirport(airportCapability, undefined, iata, column, groups).state,
    [airportCapability, groups],
  )

  const capabilityOf = useCallback(
    (iata: string) =>
      verdictCounts(fleet, (column) =>
        resolveAirport(airportCapability, exceptions.get(iata), iata, column, groups),
      ),
    [fleet, airportCapability, exceptions, groups],
  )

  // A deleted group leaves the view pointing at nothing, so it falls back to
  // whichever group is first rather than showing an empty table.
  const groupCode = view.startsWith('g:') ? view.slice(2) : ''
  const activeGroup = groups.has(groupCode) ? groupCode : (airportGroups[0]?.code ?? '')

  const rows = useMemo(() => {
    if (view === ISSUES) return withIssues
    const members = groups.get(activeGroup)
    return members ? airports.filter((row) => members.has(row.iata)) : airports
  }, [view, withIssues, groups, activeGroup, airports])

  async function saveException(iata: string, allow: AllowRow, reason: string) {
    const existing = exceptions.get(iata)
    if (Object.keys(allow).length === 0) {
      if (existing) await remove('capabilityException', existing.id)
      return
    }
    if (existing) await update('capabilityException', existing.id, { allow, reason })
    else await create('capabilityException', { scope: 'airport', subject: iata, allow, reason })
  }

  const columns = useMemo<Column<Airport>[]>(
    () => [
      {
        key: 'iata',
        header: 'IATA',
        value: (row) => row.iata,
        editor: 'text',
        isKey: true,
        cellClassName: 'w-20',
        render: (row) => <span className="t-number-sm text-ink">{row.iata}</span>,
      },
      {
        key: 'icao',
        header: 'ICAO',
        value: (row) => row.icao,
        editor: 'text',
        isKey: true,
        cellClassName: 'w-24',
        render: (row) => <span className="t-number-sm text-body">{row.icao}</span>,
      },
      { key: 'name', header: 'Name', value: (row) => row.name, editor: 'text' },
      { key: 'municipality', header: 'City', value: (row) => row.municipality, editor: 'text', cellClassName: 'w-40' },
      {
        key: 'countryCode',
        header: 'Country',
        value: (row) => row.countryCode,
        editor: 'select',
        options: countryCodes,
        cellClassName: 'w-24',
        render: (row) => <span className="t-number-sm text-ink">{row.countryCode}</span>,
      },
      {
        key: 'continent',
        header: 'Cont',
        value: (row) => row.continent,
        filter: 'select',
        editor: 'text',
        cellClassName: 'w-20',
      },
      {
        key: 'timezone',
        header: 'Timezone',
        value: (row) => row.timezone,
        editor: 'select',
        options: zoneNames,
        cellClassName: 'w-56',
      },
      {
        key: 'localNow',
        header: 'Local now',
        value: (row) => {
          const rules = zoneRules[row.timezone]
          if (!rules) return '—'
          const state = zoneStateAt(row.timezone, rules, now)
          return `${formatLocal(now, state.offsetSeconds).slice(11)} ${formatOffset(state.offsetSeconds)}`
        },
        numeric: true,
        filter: 'none',
        cellClassName: 'w-32',
      },
      {
        key: 'type',
        header: 'Type',
        value: (row) => row.type,
        render: (row) => TYPE_LABEL[row.type] ?? row.type,
        filter: 'select',
        editor: 'select',
        options: Object.keys(TYPE_LABEL),
        cellClassName: 'w-24',
      },
      {
        key: 'scope',
        header: 'Scope',
        value: (row) => row.scope,
        filter: 'none',
        editor: 'select',
        options: ['DOM', 'ITL'],
        cellClassName: 'w-20',
      },
      {
        // The Capability matrix read back, exception applied. Editing it here
        // makes an exception; the rule itself lives on that screen.
        key: 'capability',
        header: 'Aircraft',
        value: (row) => capabilityOf(row.iata).yes,
        numeric: true,
        filter: 'none',
        cellClassName: 'w-28',
        render: (row) => (
          <CapabilitySummary
            counts={capabilityOf(row.iata)}
            onEdit={editable ? () => setException(row) : undefined}
          />
        ),
      },
      // Only worth a column when that is what the list is for.
      ...(view === ISSUES
        ? [
            {
              key: 'issue',
              header: 'Issue',
              value: (row: Airport) => issueOf(row) ?? '',
              filter: 'select' as const,
              cellClassName: 'w-44',
              render: (row: Airport) => (
                <span className="text-semantic-down">{issueOf(row)}</span>
              ),
            },
          ]
        : []),
    ],
    [countryCodes, zoneNames, zoneRules, now, view, issueOf, capabilityOf, editable],
  )

  return (
    <section>
      <h1 className="t-title-lg text-ink">Airports</h1>

      <div className="mt-6">
        <ReferenceTable
          columns={columns}
          rows={rows}
          entity="Airport"
          canEdit={editable}
          rowWarning={issueOf}
          toolbar={
            <div className="flex flex-wrap rounded-[100px] bg-surface-strong p-1">
              {airportGroups.map((group) => (
                <ScopeTab
                  key={group.code}
                  active={view === ISSUES ? false : group.code === activeGroup}
                  onClick={() => setView(`g:${group.code}`)}
                  title={group.name}
                >
                  {group.code}{' '}
                  <span className="t-number-sm">{groups.get(group.code)?.size ?? 0}</span>
                </ScopeTab>
              ))}
              <ScopeTab active={view === ISSUES} onClick={() => setView(ISSUES)}>
                Issues {withIssues.length > 0 && <span className="t-number-sm">{withIssues.length}</span>}
              </ScopeTab>
            </div>
          }
          onCreate={(values) =>
            create('airport', {
              iata: values.iata.trim().toUpperCase(),
              icao: values.icao.trim().toUpperCase(),
              name: values.name.trim(),
              municipality: values.municipality.trim(),
              countryCode: values.countryCode.trim().toUpperCase(),
              continent: values.continent.trim().toUpperCase(),
              timezone: values.timezone.trim(),
              type: values.type.trim(),
              scope: values.scope.trim().toUpperCase(),
            })
          }
          onUpdate={(id, values) =>
            update('airport', id, {
              name: values.name.trim(),
              municipality: values.municipality.trim(),
              countryCode: values.countryCode.trim().toUpperCase(),
              continent: values.continent.trim().toUpperCase(),
              timezone: values.timezone.trim(),
              type: values.type.trim(),
              scope: values.scope.trim().toUpperCase(),
            })
          }
          onDelete={(id) => remove('airport', id)}
        />
      </div>

      {exception && (
        <ExceptionDialog
          subject={exception.iata}
          subtitle={exception.name}
          columns={fleet}
          baseline={baselineFor(exception.iata)}
          current={exceptions.get(exception.iata)}
          canEdit={editable}
          onClose={() => setException(null)}
          onSave={(allow, reason) => saveException(exception.iata, allow, reason)}
        />
      )}
    </section>
  )
}

function ScopeTab({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean
  onClick: () => void
  title?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      className={`t-caption-strong inline-flex items-center gap-1.5 rounded-[100px] px-3.5 py-2 transition-colors
                  ${active ? 'bg-canvas text-ink' : 'text-muted hover:text-body'}`}
    >
      {children}
    </button>
  )
}
